'use client';

import {CompaniesStep} from 'iq-onboarding/components/companies_step';
import {PositionsStep} from 'iq-onboarding/components/positions_step';
import {ResumeBranch} from 'iq-onboarding/components/resume_branch';
import {type ResumeCompleteResult} from 'iq-onboarding/components/resume_uploader';
import {SkillsetRadar} from 'iq-onboarding/components/skillset_radar';
import {StepIndicator} from 'iq-onboarding/components/step_indicator';
import {SummaryStep} from 'iq-onboarding/components/summary_step';
import {saveOnboardingAction} from 'iq-onboarding/post_auth';
import {trackSignupStepViewed, type SignupStep} from 'iq-onboarding/tracking';
import {useIsMobile} from 'common/hooks/is_mobile';
import {cn} from 'common/utils';
import type {OnboardingCompany, OnboardingPosition, TopicWeight} from 'iq-onboarding/types';
import {SignupForm} from 'iq-auth/components/signup_form';
import {AuthError} from 'iq-auth/components/auth_error';
import {useAuth} from 'iq-auth';
import {enqueuePostAuthAction} from 'iq-auth/post_auth_actions';
import {postSignupDestination} from 'iq-auth/post_signup_destination';
import {redeemAccessCodeAction} from 'iq-auth/post_auth_actions/redeem_access_code';
import {useSearchParams} from 'next/navigation';
import {Suspense, useCallback, useEffect, useState} from 'react';
import {Logo} from '@repo/ui/logo';

type Screen = 'upload' | 'positions' | 'companies' | 'summary' | 'account';
const SCREEN_ORDER: Screen[] = ['upload', 'positions', 'companies', 'summary', 'account'];

// The 'upload' screen is reported as the 'resume' tracking step.
const SCREEN_STEP: Record<Screen, SignupStep> = {
  upload: 'resume',
  positions: 'positions',
  companies: 'companies',
  summary: 'summary',
  account: 'account',
};

export function SignupPage() {
  const {user, isReady} = useAuth();
  const searchParams = useSearchParams();
  const accessCode = searchParams.get('c') ?? undefined;
  const accessCodeEmail = searchParams.get('e') ?? undefined;
  // Every new signup lands on pricing (carrying `?next=` so it can send them back). A
  // pending access code means the user is redeeming into premium, so they skip the
  // paywall and land on the dashboard once redemption runs post-auth.
  const next = postSignupDestination({
    next: searchParams.get('next'),
    hasAccessCode: !!accessCode,
  });
  const [screen, setScreen] = useState<Screen>('upload');
  const [errorDismissed, setErrorDismissed] = useState(false);
  const isMobile = useIsMobile();

  // Carried forward from resume processing / built up across the steps.
  const [resumeUuid, setResumeUuid] = useState<string | undefined>();
  const [suggestedPositionIds, setSuggestedPositionIds] = useState<number[]>([]);
  const [positions, setPositions] = useState<OnboardingPosition[]>([]);
  const [companies, setCompanies] = useState<OnboardingCompany[]>([]);
  const [currentSkillset, setCurrentSkillset] = useState<TopicWeight[]>([]);
  const [seedTargetSkillset, setSeedTargetSkillset] = useState<TopicWeight[]>([]);
  const [suggestedFirstName, setSuggestedFirstName] = useState<string | undefined>();
  const [suggestedLastName, setSuggestedLastName] = useState<string | undefined>();
  const [suggestedEmail, setSuggestedEmail] = useState<string | undefined>();

  // A logged-in user has nothing to sign up for.
  useEffect(() => {
    if (isReady && user.loggedIn) window.location.href = '/';
  }, [isReady, user.loggedIn]);

  // Carry a `?c=` access code through to redemption, which runs post-auth (so it
  // works for both password and OAuth). Enqueued up front since the code is in
  // the URL from the first render.
  useEffect(() => {
    if (accessCode) {
      enqueuePostAuthAction(redeemAccessCodeAction, {code: accessCode, email: accessCodeEmail});
    }
  }, [accessCode, accessCodeEmail]);

  // One tracking event per step the user lands on.
  useEffect(() => {
    void trackSignupStepViewed(SCREEN_STEP[screen], SCREEN_ORDER.indexOf(screen), !!resumeUuid);
  }, [screen, resumeUuid]);

  function handleResumeComplete(result: ResumeCompleteResult) {
    if (result) {
      const {resumeUuid: uuid, suggestions} = result;
      setResumeUuid(uuid);
      setSuggestedPositionIds(suggestions.positions.map((p) => p.id));
      setPositions(suggestions.positions);
      setCompanies(suggestions.companies);
      setCurrentSkillset(suggestions.current_skillset);
      setSeedTargetSkillset(suggestions.target_skillset);
      setSuggestedFirstName(suggestions.first_name ?? undefined);
      setSuggestedLastName(suggestions.last_name ?? undefined);
      setSuggestedEmail(suggestions.email ?? undefined);
    }
    setScreen('positions');
  }

  // Stable callbacks so SummaryStep's fetch effect can depend on them without re-running.
  const handleSummaryContinue = useCallback(() => {
    // Enqueue the onboarding completion so it survives an OAuth redirect and is
    // drained right after the account is created (inline, or in the OAuth callback).
    enqueuePostAuthAction(saveOnboardingAction, {
      resumeUuid,
      positions: positions.map((p) => p.id),
      companies: companies.map((c) => c.id),
    });
    setScreen('account');
  }, [resumeUuid, positions, companies]);

  const handleSummaryReview = useCallback(() => setScreen('positions'), []);

  // Step indicator: allow jumping back to an already-completed step.
  function goToStep(index: number) {
    if (index < SCREEN_ORDER.indexOf(screen)) setScreen(SCREEN_ORDER[index]!);
  }

  const screenHasRadar = screen === 'positions' || screen === 'companies';
  const radarVisible = !isMobile && (currentSkillset.length > 0 || positions.length > 0);

  function renderScreen() {
    if (screen === 'upload') {
      return (
        <div className='mx-auto w-full max-w-lg'>
          <ResumeBranch
            onComplete={handleResumeComplete}
            onFileSelected={() => setErrorDismissed(true)}
          />
        </div>
      );
    }

    if (screen === 'positions') {
      return (
        <PositionsStep
          suggestedIds={suggestedPositionIds}
          value={positions}
          onChange={setPositions}
          onContinue={() => setScreen('companies')}
        />
      );
    }

    if (screen === 'companies') {
      return (
        <CompaniesStep
          value={companies}
          onChange={setCompanies}
          onContinue={() => setScreen('summary')}
        />
      );
    }

    if (screen === 'summary') {
      return (
        <SummaryStep
          positions={positions}
          companies={companies}
          onContinue={handleSummaryContinue}
          onReview={handleSummaryReview}
        />
      );
    }

    return (
      <div className='mx-auto w-full max-w-lg bg-white rounded-2xl p-8'>
        <SignupForm
          next={next}
          initialEmail={suggestedEmail ?? accessCodeEmail}
          initialFirstName={suggestedFirstName}
          initialLastName={suggestedLastName}
        />
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-iq-black flex flex-col gap-8'>
      <header className='flex flex-col items-center gap-8'>
        <StepIndicator
          step={SCREEN_ORDER.indexOf(screen)}
          totalSteps={SCREEN_ORDER.length}
          onStepClick={goToStep}
        />
        <Logo href='/' inverted />
      </header>
      <main className='flex-1 flex justify-center p-8'>
        <div className='w-full max-w-5xl'>
          <div className='flex flex-col gap-10'>
            {screen === 'upload' && (
              <div className='mx-auto w-full max-w-lg empty:hidden'>
                <Suspense>
                  <AuthError variant='dark' dismissed={errorDismissed} />
                </Suspense>
              </div>
            )}
            {screenHasRadar ? (
              <div className='lg:overflow-x-clip'>
                <div className='lg:grid lg:grid-cols-[minmax(0,32rem)_28rem] lg:items-start lg:justify-center lg:gap-12'>
                  <div
                    className={cn(
                      'lg:transition-transform lg:duration-500 lg:ease-out',
                      radarVisible ? 'lg:translate-x-0' : 'lg:translate-x-[15.5rem]',
                    )}>
                    <div
                      key={screen}
                      className='mx-auto w-full max-w-lg animate-in fade-in slide-in-from-right-4 duration-300'>
                      {renderScreen()}
                    </div>
                  </div>
                  {radarVisible && (
                    <div className='hidden lg:block lg:sticky lg:top-8 animate-in fade-in slide-in-from-left-8 duration-500'>
                      <SkillsetRadar
                        current={currentSkillset}
                        initialTargetSkillset={seedTargetSkillset}
                        positionSlugs={positions.map((p) => p.slug)}
                        companySlugs={companies.map((c) => c.slug)}
                      />
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div key={screen} className='animate-in fade-in slide-in-from-right-4 duration-300'>
                {renderScreen()}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
